import { useApolloClient } from '@apollo/client'
import { useEffect } from 'react'

import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager'
import { Collection, Variant } from '../../types/graphql'
import { GET_PRODUCT_IMPRESSION_VARIANTS } from '../graphql/queries/GET_PRODUCT_IMPRESSION_VARIANTS'

/**
 * Request variant information & track product impressions for all of the items in a collection.
 *
 * This (essentially) stateless hook exists to avoid unnecessary rerenders for a collection page.
 * By encapsulating the data loading & tracking behavior associated with tracking product impressions,
 * we are able to avoid re-renders for functionality that is not related  to the presentation of the collections page.
 */
export function useCollectionViewProductImpressions({
  collectionViewHandle,
  sectionId,
}: {
  collectionViewHandle: string
  sectionId: string
}) {
  const client = useApolloClient()
  const { pushProductImpressions } = useGoogleTagManager()

  useEffect(() => {
    const handleProductImpressions = async () => {
      // Query to get the default variants for the items in a given collection/sectionId
      const result = await client.query<{
        collectionView: {
          section: {
            collection: Collection | null
          } | null
        } | null
      }>({
        query: GET_PRODUCT_IMPRESSION_VARIANTS,
        variables: {
          collectionViewHandle,
          sectionId,
        },
      })

      const { data, error } = result
      const collectionHandle = data?.collectionView?.section?.collection?.handle
      const items = data?.collectionView?.section?.collection?.items?.nodes

      // If we receive items from the query, push product impressions
      if (!error && items && items.length > 0) {
        const variants = items.map(o => o.defaultVariant).filter((v): v is Variant => Boolean(v))
        if (variants.length === 0) return

        pushProductImpressions({
          variants,
          list: collectionHandle,
        })
      }
    }

    // Early escape hatch, TypeScript should make this not necessary to have... but we lie and don't rely on generated types for the collection page queries. Sometimes bad data sneaks in here.
    if (!collectionViewHandle || !sectionId) return

    handleProductImpressions()
  }, [sectionId, client, pushProductImpressions, collectionViewHandle])
}
